/**
 * PM - Casillas del bloque Disponibilidad (Collection/Search). Rediseño
 * 2026-10-01: cuatro casillas independientes (Disponibles, Preventa, Compra
 * internacional, Agotados), con cualquier combinación salvo todas apagadas.
 *
 * Traduce las casillas a los parámetros nativos que assets/facets.js (sin
 * modificar) lee del formulario FacetsFilterForm:
 *   filter.v.availability                  1 / 0 / ausente
 *   filter.p.m.promusic.commercial_status  preventa / compra_internacional
 *     (filtro de Search & Discovery sobre el metafield de producto)
 *
 *   Selección (D, P, I, A)               Servidor                 Navegador
 *   solo A                               availability=0           -
 *   D (+ P/I/A opcionales)               availability=1 o nada    oculta P/I no elegidas
 *   P y/o I, sin D ni A                  availability=1 + estado  -
 *   P y/o I + A, sin D (raro)            nada                     oculta disponibles normales
 *                                                                 y P/I no elegidas
 * Lo que se oculta en el navegador son pocas tarjetas (preventa / compra
 * internacional son una fracción del catálogo), salvo el caso raro.
 *
 * CONTADOR (#ProductCount, 2026-10-01): cuando hay tarjetas ocultas en el
 * navegador, el número del servidor no las descuenta; en ese caso se
 * reemplaza por la suma de los conteos de las filas marcadas (los mismos
 * que muestra el bloque). Usa los nodos ocultos que ya renderiza
 * collection-actions / search-actions (data-pm-count-singular-text,
 * data-pm-count-plural-before/after) para respetar singular/plural y el
 * texto de búsqueda. Sin ocultamiento, no se toca (manda el servidor).
 *
 * Tipo de tarjeta ([data-pm-product-card], main-collection-product-grid /
 * main-search): badge de snippets/pm-commercial-info.liquid en contexto card
 * (.pm-commercial-info--preventa / --intl) y data-pm-priority="0" para
 * agotados.
 *
 * El estado de ocultamiento vive en memoria (window.__pmAvailHide): sobrevive
 * a las respuestas AJAX de facets.js (otros filtros, orden, páginas), se
 * reinicia en cada carga real de página y con "Borrar filtros".
 */
(function () {
  if (!window.__pmAvailHide) {
    window.__pmAvailHide = { normal: false, preventa: false, intl: false };
  }

  function resetHide() {
    window.__pmAvailHide = { normal: false, preventa: false, intl: false };
  }

  function anyHide() {
    const h = window.__pmAvailHide;
    return h.normal || h.preventa || h.intl;
  }

  function cardType(card) {
    if (card.querySelector('.pm-commercial-info--preventa.pm-commercial-info--card')) return 'preventa';
    if (card.querySelector('.pm-commercial-info--intl.pm-commercial-info--card')) return 'intl';
    if (card.getAttribute('data-pm-priority') === '0') return 'agotado';
    return 'normal';
  }

  // --- Contador ---------------------------------------------------------

  function readCount(key) {
    const node = document.querySelector('[data-pm-count="' + key + '"]');
    if (!node || node.hasAttribute('data-pm-pending')) return null;
    const value = Number.parseInt(node.textContent.trim(), 10);
    return Number.isNaN(value) ? null : value;
  }

  function rowChecked(selector) {
    const input = document.querySelector(selector);
    if (!input) return false;
    const row = input.closest('li');
    if (row && row.hidden) return false;
    return input.checked;
  }

  function refreshProductCount() {
    if (!anyHide()) return;
    const container = document.getElementById('ProductCount');
    const textNode = container ? container.querySelector('[data-pm-count-text]') : null;
    if (!textNode) return;

    const parts = [
      ['disponible', '[data-pm-disponibles-toggle]'],
      ['preventa', '[data-pm-preventa-toggle]'],
      ['intl', '[data-pm-intl-toggle]'],
      ['agotado', '[data-pm-agotados-toggle]'],
    ];
    let total = 0;
    for (let index = 0; index < parts.length; index += 1) {
      const [key, selector] = parts[index];
      if (!rowChecked(selector)) continue;
      const value = readCount(key);
      if (value === null) return; // pendiente: se reintenta al llegar el dato
      total += value;
    }

    const singular = container.querySelector('[data-pm-count-singular-text]');
    const before = container.querySelector('[data-pm-count-plural-before]');
    const after = container.querySelector('[data-pm-count-plural-after]');
    let text = null;
    if (total === 1 && singular) {
      text = singular.textContent;
    } else if (before && after) {
      text = before.textContent + total + after.textContent;
    }
    if (text !== null && textNode.textContent !== text) {
      textNode.textContent = text;
    }
  }

  // --- Ocultamiento -----------------------------------------------------

  function applyHide() {
    const h = window.__pmAvailHide;
    document.querySelectorAll('[data-pm-product-card]').forEach((card) => {
      const type = cardType(card);
      const hide = (type === 'normal' && h.normal) || (type === 'preventa' && h.preventa) || (type === 'intl' && h.intl);
      if (hide) {
        card.hidden = true;
        card.setAttribute('data-pm-avail-hidden', '');
      } else if (card.hasAttribute('data-pm-avail-hidden')) {
        card.hidden = false;
        card.removeAttribute('data-pm-avail-hidden');
      }
    });
    refreshProductCount();
  }

  // El grid y #ProductCount se reemplazan o crecen vía AJAX (filtros, orden,
  // "cargar más"): se reaplica cuando cambian.
  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled || !anyHide()) return;
    scheduled = true;
    window.requestAnimationFrame(() => {
      scheduled = false;
      applyHide();
    });
  });
  function startObserver() {
    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    }
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startObserver, { once: true });
  } else {
    startObserver();
  }

  document.addEventListener('pm:availability-counts-updated', refreshProductCount);

  document.addEventListener('click', (event) => {
    if (event.target.closest('[data-pm-clear-all]')) {
      resetHide();
      applyHide();
    }
  });

  class PmAvailabilityToggle extends HTMLElement {
    connectedCallback() {
      this.native = this.querySelector('[data-pm-availability-native]');
      this.mfPre = this.querySelector('[data-pm-mf-preventa]');
      this.mfIntl = this.querySelector('[data-pm-mf-intl]');
      this.d = this.querySelector('[data-pm-disponibles-toggle]');
      this.p = this.querySelector('[data-pm-preventa-toggle]');
      this.i = this.querySelector('[data-pm-intl-toggle]');
      this.a = this.querySelector('[data-pm-agotados-toggle]');

      if (!this.native || !this.a) {
        return;
      }

      // Casillas que el servidor no puede reflejar en la URL (ocultas en el
      // navegador): se restaura su estado visual tras cada render AJAX.
      const h = window.__pmAvailHide;
      if (h.normal && this.d) this.d.checked = false;
      if (h.preventa && this.p) this.p.checked = false;
      if (h.intl && this.i) this.i.checked = false;
      applyHide();

      this.onToggleChange = this.onToggleChange.bind(this);
      [this.d, this.p, this.i, this.a].forEach((input) => {
        if (input) input.addEventListener('change', this.onToggleChange);
      });
    }

    disconnectedCallback() {
      [this.d, this.p, this.i, this.a].forEach((input) => {
        if (input) input.removeEventListener('change', this.onToggleChange);
      });
    }

    snapshot() {
      return [
        this.native.checked,
        this.native.value,
        this.mfPre ? this.mfPre.checked : '',
        this.mfIntl ? this.mfIntl.checked : '',
      ].join('|');
    }

    onToggleChange(event) {
      const d = this.d ? this.d.checked : true;
      // Fila ausente/oculta (0 productos de ese tipo): sigue a Disponibles.
      const pRow = this.p && !this.p.closest('li').hidden;
      const iRow = this.i && !this.i.closest('li').hidden;
      const p = pRow ? this.p.checked : d;
      const i = iRow ? this.i.checked : d;
      const a = this.a.checked;

      if (!d && !p && !i && !a) {
        if (event && event.target) event.target.checked = true;
        return;
      }

      let avail = null; // null = sin parámetro
      let mfPre = false;
      let mfIntl = false;
      const hide = { normal: false, preventa: false, intl: false };

      if (!d && !p && !i) {
        avail = '0';
      } else if (d) {
        avail = a ? null : '1';
        hide.preventa = !p;
        hide.intl = !i;
      } else if (!a) {
        avail = '1';
        mfPre = p;
        mfIntl = i;
      } else {
        hide.normal = true;
        hide.preventa = !p;
        hide.intl = !i;
      }

      window.__pmAvailHide = hide;

      const before = this.snapshot();
      if (avail === null) {
        this.native.checked = false;
      } else {
        this.native.value = avail;
        this.native.checked = true;
      }
      if (this.mfPre) this.mfPre.checked = mfPre;
      if (this.mfIntl) this.mfIntl.checked = mfIntl;

      if (this.snapshot() !== before) {
        this.native.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        applyHide();
      }
    }
  }

  if (!customElements.get('pm-availability-toggle')) {
    customElements.define('pm-availability-toggle', PmAvailabilityToggle);
  }
})();
